import React, { MouseEventHandler } from "react";
import { Connection, Provider } from "../common/ObjectTypes";
import { useTranslation } from "react-i18next";
import { getConnectionStatus } from "./getConnectionStatus";
import { Switch } from "@headlessui/react";
import { AttentionIcon, ToolTipIcon } from "../common/ProjectIcons";
import { Tooltip } from "../common/Tooltip";
import { containerClasses, connectionDetailsClasses } from "../common/tailwindClassnames";

type ConnectionDetailsProps = {
    reloadConnection: boolean,
    connection: Connection,
    connectionHasDestinations: boolean,
    connectionProvider: Provider,
    subPage: string | undefined,
    onSelectSubPage: Function,
    onChangeProviderEnabled: Function,
}

const ConnectionDetails = ({
    reloadConnection,
    connection,
    connectionHasDestinations,
    connectionProvider,
    subPage,
    onSelectSubPage,
    onChangeProviderEnabled
}: ConnectionDetailsProps) => {
    const { t } = useTranslation();
    const connectionStatus = getConnectionStatus(connection.status, true, t);

    return (
        <div className={connectionDetailsClasses.container}>
            <div className={connectionDetailsClasses.header}>
                {/* page header */}
                <div className="flex flex-row justify-between">
                    <div className="flex flex-col w-3/4">
                        <div className="flex flex-row items-center"><h1 className={connectionDetailsClasses.title}>{connection.provider.name}</h1> {(!reloadConnection && connectionProvider.enabled) && <p className={`mx-3 ${connectionStatus.style}`}>{connectionStatus.text}</p>}</div>
                        <div className="flex flex-row items-center">
                            <label className={connectionDetailsClasses.infoLabel}>{t("Workspace")}: </label><label className={connectionDetailsClasses.infoPill}>{connection?.owner?.name || ''}</label>
                            <label className={connectionDetailsClasses.separator}>&#x2022;</label>
                            <label className={connectionDetailsClasses.infoLabel}>{t("Technology")}: </label><label className={connectionDetailsClasses.infoPill}>{connection?.provider?.type?.name || ''}</label>
                        </div>
                    </div>
                    <div className={connectionProvider.enabled ? connectionDetailsClasses.enabledPanel : connectionDetailsClasses.disabledPanel}>
                        <p className="text-left text-sm">
                            {connectionProvider.enabled
                                ? <span className={connectionDetailsClasses.switchText}>
                                    <span className={connectionDetailsClasses.switchTextLabel}>{t("Connection Enabled")}</span>
                                    <span className="flex items-center translate-y-1">
                                        <Tooltip description={t("Sending data to its destinations")} position="bottom" />
                                    </span>
                                </span>
                                : <span className={connectionDetailsClasses.switchText}>
                                    <span className={connectionDetailsClasses.switchTextLabel}>{t("Connection Disabled")}</span>
                                    <span className="flex items-center translate-y-1">
                                        <Tooltip description={t("Not sending data to its destinations")} position="bottom" />
                                    </span>
                                </span>
                            }
                        </p>
                        <div className="w-16">
                            <Switch
                                checked={connectionProvider.enabled}
                                onChange={(e) => onChangeProviderEnabled(e)}
                                className={`
                                    ${connectionDetailsClasses.switchContainer}
                                    ${connectionProvider.enabled
                                        ? 'bg-status-green focus:ring-status-green/50'
                                        : 'bg-secondary-gray focus:ring-secondary-gray/50'
                                    } 
                                `}
                            >
                                <span className="sr-only">{connectionProvider.enabled ? t('Enabled') : t('Disabled')}</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="absolute right-2 top-1.5 h-4 w-4 text-white transition-opacity duration-200"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>

                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="absolute left-2 top-1.5 h-4 w-4 text-white transition-opacity duration-200"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>

                                {/* The moving button (without icons) */}
                                <span
                                    aria-hidden="true"
                                    className="pointer-events-none inline-block size-5 translate-x-0 rounded-full bg-white ring-0 shadow-lg transition duration-200 ease-in-out group-data-[checked]:translate-x-7"
                                />
                            </Switch>
                        </div>

                    </div>
                </div>
                {/* tabs for sub-pages */}
                <div className="w-full flex flex-row font-semibold">
                    <label className={subPage === "overview" ? connectionDetailsClasses.tabActive : connectionDetailsClasses.tabInactive}
                        onClick={() => {
                            onSelectSubPage("")
                        }}>
                        {t("Overview")}
                    </label>
                    <label className={subPage === "provider" ? connectionDetailsClasses.tabActive : connectionDetailsClasses.tabInactive.replace('mr-3', 'mx-3')}
                        onClick={() => {
                            onSelectSubPage("provider")
                        }}>
                        {t("Provider")}
                    </label>
                    <label className={subPage === "destinations" ? connectionDetailsClasses.tabActive.replace('mr-3', 'mx-3') : connectionDetailsClasses.tabInactive.replace('mr-3', 'mx-3')}
                        onClick={() => {
                            onSelectSubPage("destinations")
                        }}>
                        <span className="flex flex-row items-baseline">{!connectionHasDestinations && <div className="mr-1.5 transform -translate-y-0.5"><AttentionIcon /></div>}<div className="">{t("Destinations")}</div></span>
                    </label>
                    <label className={subPage === "sources" ? connectionDetailsClasses.tabActive.replace('mr-3', 'mx-3') : connectionDetailsClasses.tabInactive.replace('mr-3', 'mx-3')}
                        onClick={() => {
                            onSelectSubPage("sources")
                        }}>
                        {t("Sources")}
                    </label>
                    <label className={subPage === "logs" ? connectionDetailsClasses.tabActive.replace('mr-3', 'mx-3') : connectionDetailsClasses.tabInactive.replace('mr-3', 'mx-3')}
                        onClick={() => {
                            onSelectSubPage("logs")
                        }}>
                        {t("Logs")}
                    </label>

                </div>
            </div>
        </div>
    );
};

export default ConnectionDetails;